import { useState, useEffect, useCallback } from 'react';
import { storage, STORAGE_KEYS } from '@/lib/storage';

type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

/**
 * React hook for localStorage state with automatic persistence
 * 
 * @example
 * const [sidebarOpen, setSidebarOpen] = useLocalStorage(STORAGE_KEYS.MANUAL_SIDEBAR_OPEN, false);
 * 
 * @example with toggle
 * const [isOpen, setIsOpen, toggle] = useLocalStorage(STORAGE_KEYS.SIDEBAR_COLLAPSED, true);
 * <Button onClick={toggle}>Toggle</Button>
 */
export function useLocalStorage<T>(
  key: StorageKey,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    return storage.get(key, defaultValue);
  });

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStoredValue(prev => {
      const valueToStore = value instanceof Function ? value(prev) : value;
      storage.set(key, valueToStore);
      return valueToStore;
    });
  }, [key]);

  // Toggle helper for boolean values
  const toggle = useCallback(() => {
    if (typeof storedValue === 'boolean') {
      setValue(!storedValue as T);
    }
  }, [storedValue, setValue]);

  // Sync with other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue, toggle];
}

/**
 * Simplified hook for boolean toggles
 * 
 * @example
 * const [isOpen, toggle] = useLocalStorageToggle(STORAGE_KEYS.SIDEBAR_OPEN, false);
 * <Button onClick={toggle}>Toggle</Button>
 */
export function useLocalStorageToggle(
  key: StorageKey,
  defaultValue: boolean
): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue, toggle] = useLocalStorage(key, defaultValue);
  return [value, toggle, setValue];
}
